import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, ArrowUpRight, Scale, Check } from "lucide-react";
import { toast } from "sonner";
import { formatPrice } from "@/lib/format";
import { VERTICALS } from "@/lib/perspective";
import { useCompare } from "@/lib/useCompare";
import { imgSrc } from "@/lib/api";

export const PropertyCard = ({ property, index = 0 }) => {
  const { ids, toggle } = useCompare();
  const inCompare = ids.includes(property.id);

  const handleCompare = (e) => {
    e.preventDefault();
    const res = toggle(property.id);
    if (res.full) toast.error("You can compare up to 3 properties.");
    else if (res.added) toast.success(`${property.project} added to compare`);
    else toast(`${property.project} removed from compare`);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 26 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay: (index % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
      className="group arch-card hover-lift overflow-hidden"
      data-testid={`property-card-${property.id}`}
    >
      <Link to={`/properties/${property.id}`} className="block">
        <div className="img-zoom relative aspect-[4/3] overflow-hidden">
          <img src={imgSrc(property.images?.[0])} alt={property.project} loading="lazy" className="h-full w-full object-cover" />
          <div className="absolute inset-x-0 top-0 flex items-start justify-between p-3">
            <div className="flex flex-wrap gap-1.5">
              {property.category?.map((c) => (
                <span key={c} className="rounded-full bg-black/45 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-white backdrop-blur">
                  {VERTICALS[c]?.name || c}
                </span>
              ))}
            </div>
            <div className="flex gap-1.5">
              <button onClick={handleCompare} data-testid={`compare-btn-${property.id}`} aria-label="Compare" className={`flex h-8 w-8 items-center justify-center rounded-full backdrop-blur transition ${inCompare ? "bg-[var(--accent-gold)] text-[#071711]" : "bg-black/45 text-white hover:bg-[var(--accent-gold)] hover:text-[#071711]"}`}>
                {inCompare ? <Check className="h-4 w-4" /> : <Scale className="h-4 w-4" />}
              </button>
            </div>
          </div>
          {property.featured && (
            <span className="absolute bottom-3 left-3 rounded-full bg-[var(--accent-gold)] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-[#071711]">Featured</span>
          )}
        </div>

        <div className="p-6">
          <div className="flex items-center gap-1.5 text-[var(--text-muted)]">
            <MapPin className="h-3.5 w-3.5" />
            <span className="text-xs">{property.location} · {property.microMarket}</span>
          </div>
          <h3 className="mt-2 font-serif text-2xl font-medium leading-tight text-[var(--text-primary)]">{property.project}</h3>
          <p className="mt-1 text-sm text-[var(--text-muted)]">{property.developer}</p>

          <div className="mt-4 flex flex-wrap gap-2">
            {property.configuration?.slice(0, 3).map((cf) => (
              <span key={cf} className="rounded-md border border-[var(--border-subtle)] px-2 py-0.5 text-xs text-[var(--text-muted)]">{cf}</span>
            ))}
          </div>

          <div className="mt-5 flex items-end justify-between border-t border-[var(--border-subtle)] pt-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[var(--text-muted)]">Starting</div>
              <div className="font-serif text-xl text-[var(--text-primary)]">{formatPrice(property.priceMin)}</div>
              <div className="mt-0.5 text-xs text-[var(--text-muted)]">{property.possession}</div>
            </div>
            <span className="inline-flex items-center gap-1 text-sm font-medium text-[var(--accent-gold)] transition-transform duration-300 group-hover:translate-x-1">
              Explore Perspective <ArrowUpRight className="h-4 w-4" />
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export default PropertyCard;
